import React, { FunctionComponent, useMemo } from 'react';
import { IconButton, useSafeIntl } from 'bluesquare-components';
import { TableWithDeepLink } from 'Iaso/components/tables/TableWithDeepLink';
import { useGetCountryUsersGroup, useGetProfiles } from '../requests';
import { CountryNotificationsConfigModal } from '../CountryNotificationsConfigModal';
import { baseUrls } from '../../../../constants/urls';
import MESSAGES from '../../../../constants/messages';

const makeUserNameToDisplay = user => {
    if (user.email) return ` ${user.email}`;
    if (user.first_name && user.last_name)
        return ` ${user.first_name} ${user.last_name}`;
    return ` ${user.username}`;
};

const allLanguages = [
    { value: 'EN', label: 'EN' },
    { value: 'FR', label: 'FR' },
    { value: 'PT', label: 'PT' },
];
type Props = {
    params: Record<string, any>;
};
export const CountryNotificationsConfigTable: FunctionComponent<Props> = ({
    params,
}) => {
    const { formatMessage } = useSafeIntl();
    const tableParams = useMemo(
        () => ({
            order: params.order ?? 'country__name', // Watch out, needs 2 underscores
            page: params.page ?? 1,
            pageSize: params.pageSize ?? 50,
        }),
        [params.order, params.page, params.pageSize],
    );

    const { data: allUsers } = useGetProfiles();
    const { data: tableData, isLoading } = useGetCountryUsersGroup(tableParams);

    const columns = [
        {
            Header: formatMessage(MESSAGES.country),
            id: 'country__name',
            accessor: 'country_name', // Watch out, needs 2 underscores
            sortable: true,
            align: 'left',
        },
        {
            Header: formatMessage(MESSAGES.usersToNotify),
            accessor: 'read_only_users_field',
            width: 100,
            sortable: false,
            align: 'left',
            Cell: settings =>
                settings.value.map(makeUserNameToDisplay).toString().trim(),
        },
        {
            Header: formatMessage(MESSAGES.language),
            sortable: true,
            accessor: 'language',
        },
        {
            Header: formatMessage(MESSAGES.actions),
            sortable: false,
            accessor: 'actions',
            Cell: settings => {
                return (
                    <CountryNotificationsConfigModal
                        countryId={settings.row.original.id}
                        countryName={settings.row.original.country_name}
                        language={settings.row.original.language}
                        users={settings.row.original.users}
                        teams={settings.row.original.teams}
                        allUsers={allUsers?.profiles}
                        allLanguages={allLanguages}
                        renderTrigger={({ openDialog }) => (
                            <IconButton
                                onClick={() => {
                                    openDialog();
                                }}
                                icon="edit"
                                tooltipMessage={MESSAGES.edit}
                                size="small"
                            />
                        )}
                    />
                );
            },
        },
    ];
    return (
        <TableWithDeepLink
            data={tableData?.country_users_group ?? []}
            params={tableParams}
            columns={columns}
            baseUrl={baseUrls.countryConfig}
            pages={tableData?.pages ?? 1}
            count={tableData?.count ?? 1}
            multiselect={false}
            extraProps={{
                loading: isLoading,
            }}
        />
    );
};
